// Needs suggestions and risk vulnerability (spec §8–§11). Suggestions are rules of thumb the advisor
// accepts, edits or ignores — each carries the reason it was proposed. Nothing is written by itself.
import { ageOn, coverageForPerson, type Day, type NeedArea, type NeedLine, type NeedsProfile, type WorkspaceView } from '@vero/domain';
import { coverAreas, coverageProfile } from './coverage.ts';

export interface NeedSuggestion extends NeedLine { basis: string; params?: Record<string, string | number> }

export function suggestNeeds(v: WorkspaceView, personId: string, profile: NeedsProfile, today: Day): NeedSuggestion[] {
  const p = v.get('persons', personId)!;
  const cov = coverageProfile(v, personId, today);
  const age = p.birthDate ? ageOn(p.birthDate, today) : null;
  const income = Number(profile.annualIncome) || 0;
  const deps = Number(profile.dependants ?? cov.minors) || 0;
  const mortgage = Number(profile.mortgage) || 0;
  const debts = Number(profile.liabilities) || 0;
  const savings = Number(profile.savings) || 0;
  const working = age === null || (age >= 18 && age < 67);
  const partner = /married|παντρ|εγγαμ/i.test(p.maritalStatus ?? '') || Number(profile.partnerIncome) > 0;
  // existing cover: sums insured of the policies that cover this person today (coverageForPerson), by area
  const existing: Partial<Record<NeedArea, number>> = {};
  for (const c of coverageForPerson(v, personId, today)) {
    const pol = c.policy;
    // the six coverage areas share their names with need areas (life, income, health, savings, education, property)
    for (const a of coverAreas(c)) if (pol.sumAssured) existing[a as NeedArea] = (existing[a as NeedArea] ?? 0) + pol.sumAssured;
  }
  const s: NeedSuggestion[] = [];
  const push = (area: NeedArea, priority: 0 | 1 | 2 | 3, target: number | null, basis: string, params?: Record<string, string | number>) =>
    s.push({ area, priority, target: target === null ? null : Math.round(target / 1000) * 1000, existing: existing[area] ?? (area === 'health' && cov.areas.find((x) => x.area === 'health')!.held ? null : 0), reasoning: null, action: null, basis, params });
  push('life', deps ? 3 : partner ? 2 : working ? 1 : 0, deps || partner ? Math.max(0, income * 10 + debts - savings) : debts || null, deps ? 'needs.basis.life_dependants' : 'needs.basis.life_debts', { years: 10 });
  push('mortgage', mortgage ? 3 : 0, mortgage || null, 'needs.basis.mortgage');
  push('income', working && income ? (deps || partner ? 3 : 2) : 0, working && income ? (income / 12) * 0.6 : null, 'needs.basis.income', { pct: 60 });
  push('critical_illness', working ? (deps ? 3 : 2) : 1, income ? income * 2 : null, 'needs.basis.critical_illness', { years: 2 });
  push('health', 3, null, 'needs.basis.health');
  push('accident', working ? 1 : 0, income || null, 'needs.basis.accident');
  push('retirement', age !== null && age >= 40 ? 3 : working ? 2 : 1, null, 'needs.basis.retirement', { age: Number(profile.retirementAge) || 65 });
  push('savings', 1, null, 'needs.basis.savings');
  push('education', cov.minors ? 2 : 0, null, 'needs.basis.education', { children: cov.minors });
  push('property', 1, null, 'needs.basis.property');
  push('business', profile.businessOwner ? 2 : 0, null, 'needs.basis.business');
  return s;
}

export interface Vulnerability { key: string; level: 'high' | 'medium' | 'low'; params?: Record<string, string | number> }
/** Explainable financial-vulnerability indicators for the risk profile (spec §11). */
export function vulnerability(profile: NeedsProfile, dependants: number): Vulnerability[] {
  const out: Vulnerability[] = [];
  const income = Number(profile.annualIncome) || 0;
  const partner = Number(profile.partnerIncome) || 0;
  const debt = (Number(profile.liabilities) || 0) + (Number(profile.mortgage) || 0);
  const monthly = Number(profile.monthlyExpenses) || 0;
  const savings = Number(profile.savings) || 0;
  if (dependants && income && partner < income * 0.25) out.push({ key: 'risk.single_income', level: 'high', params: { dependants } });
  if (income && debt / income >= 3) out.push({ key: 'risk.debt_exposure', level: debt / income >= 5 ? 'high' : 'medium', params: { ratio: Math.round((debt / income) * 10) / 10 } });
  if (monthly) { const months = savings / monthly; if (months < 3) out.push({ key: 'risk.low_buffer', level: months < 1 ? 'high' : 'medium', params: { months: Math.round(months * 10) / 10 } }); }
  if (profile.smoker) out.push({ key: 'risk.smoker', level: 'medium' });
  if (profile.businessOwner) out.push({ key: 'risk.business_owner', level: 'medium' });
  return out;
}
